import { useEffect, useRef, useState, type MouseEvent } from "react";
import { Link } from "react-router-dom";
import { logout as apiLogout } from "../lib/api";
import {
  clearSession,
  displayName,
  initials,
  useSession,
} from "../lib/auth";
import { useT, useLp } from "../lib/i18n";

export function UserMenu() {
  const session = useSession();
  const t = useT();
  const lp = useLp();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    // `click` (not `mousedown`) so React's onClick on inner buttons runs
    // first and isn't clobbered by this listener closing the menu.
    const onDocClick = (e: Event) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("click", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!session) return null;

  const handleLogout = (e: MouseEvent<HTMLButtonElement>) => {
    // Don't let the click bubble to the outside-click listener while we
    // rip the menu out of the tree.
    e.stopPropagation();
    // Clear locally first so the UI updates immediately — the component
    // unmounts via useSession returning null. Token revocation is best-
    // effort in the background; if it fails, the local session is already
    // gone and the refresh token would expire naturally anyway.
    const refreshToken = session.tokens.refresh_token;
    clearSession();
    apiLogout(refreshToken).catch(() => {});
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2 rounded-full border border-ink/15 dark:border-paper/20 py-1 pl-1 pr-3 text-sm text-ink dark:text-paper hover:bg-ink/5 dark:hover:bg-paper/5 transition-colors"
      >
        <span
          aria-hidden="true"
          className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-graphite text-paper text-[11px] font-mono uppercase"
        >
          {initials(session)}
        </span>
        <span className="max-w-[10rem] truncate">{displayName(session)}</span>
        <svg
          aria-hidden="true"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`h-3.5 w-3.5 text-ink/50 dark:text-paper/60 transition-transform ${open ? "rotate-180" : ""}`}
        >
          <polyline points="5,8 10,13 15,8" />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-60 z-50 origin-top-right rounded-xl border border-margin dark:border-paper/10 bg-paper dark:bg-ink shadow-lg overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-margin dark:border-paper/10">
            <div className="text-sm font-medium text-ink dark:text-paper truncate">
              {displayName(session)}
            </div>
            {session.email && session.email !== displayName(session) && (
              <div className="mt-0.5 text-xs text-ink/60 dark:text-paper/60 truncate">
                {session.email}
              </div>
            )}
          </div>

          <Link
            to={lp + "/account"}
            role="menuitem"
            onClick={() => setOpen(false)}
            className="block w-full text-left px-4 py-2.5 text-sm text-ink dark:text-paper hover:bg-ink/5 dark:hover:bg-paper/5 transition-colors"
          >
            {t.userMenu.account}
          </Link>

          <div className="h-px bg-margin dark:bg-paper/10" />

          <button
            type="button"
            role="menuitem"
            onClick={handleLogout}
            className="w-full text-left px-4 py-2.5 text-sm text-ink dark:text-paper hover:bg-ink/5 dark:hover:bg-paper/5 transition-colors"
          >
            {t.userMenu.signOut}
          </button>
        </div>
      )}
    </div>
  );
}
